import moment from 'moment';
// @ts-ignore
import { merge } from 'lodash-es';
import { getFormatNumPlus, getFormatPercentPlus, toFirstUpperCase, getValidDate } from '@gp/utils/common';
import { BASE_ECHART_OPTION, LEGEND_GRID_SPACING, Y_AXIS_NAME_CURRENCY_OPTION, Y_AXIS_NAME_CURRENCY_GRID_OPTION } from '@gp/constants/echarts';

import budgetSvg from '@gp/assets/svg/icon-budget.svg';
import forecastSvg from '@gp/assets/svg/icon-forecast.svg';

/**
 * 解析双轴柱线图series的业务配置，生成 用于组装双轴柱线图静态配置项
 * @param serieMetadatas 双轴柱线图series的业务配置，比如说配置了serie的取值字段，value formatter啊等
 * @returns 用于组装双轴柱线图静态配置项
 */
const analysisSerieMetadatas = (
  serieMetadatas: ISerieMetadataOfDoubleYAxisBarLine[],
): {
  seriesKeys: string[];
  series: IEChartSeriesOption[];
  yAxisValueFormattersMap: {
    [key: string]: (...params: any[]) => any;
  };
} => {
  const seriesKeys: string[] = [];
  const series: IEChartSeriesOption[] = [];
  const yAxisValueFormattersMap: {
    [key: string]: (...params: any[]) => any;
  } = {};
  if (Array.isArray(serieMetadatas)) {
    for (const serieMetadata of serieMetadatas) {
      const { key, serie, yAxisValueFormatter } = serieMetadata || {};
      if (!key || seriesKeys.includes(key)) continue;
      seriesKeys.push(key);
      series.push(serie);
      yAxisValueFormattersMap[key] = yAxisValueFormatter;
    }
  }

  return {
    seriesKeys,
    series,
    yAxisValueFormattersMap,
  };
};

/**
 * 获取双轴柱线图 图例的默认选中状态
 * @param serieMetadatas 双轴柱线图series的业务配置，比如说配置了serie的取值字段，value formatter啊等
 * @returns 双轴柱线图 图例的默认选中状态
 */
const getChartLegendSelected = (
  serieMetadatas: ISerieMetadataOfDoubleYAxisBarLine[],
): { [key: string]: boolean } => {
  const legendSelected: {
    [key: string]: boolean;
  } = {};
  if (Array.isArray(serieMetadatas)) {
    for (const serieMetadata of serieMetadatas) {
      const { key } = serieMetadata || {};
      if (!key || Reflect.has(legendSelected, key)) continue;
      Reflect.set(legendSelected, key, true);
    }
  }
  return legendSelected;
};

/**
 * 获取双轴柱线图静态配置项，即直接塞给echarts的配置项，静态配置 不包含 依赖源数据的配置
 * @param options 其他选项
 * @returns 双轴柱线图顶层配置项
 */

const getChartStaticOptionData = (options: {
  serieNamesMap: {
    [key: string]: string;
  };
  serieIconsMap: {
    [key: string]: string;
  };
  serieMetadatas: ISerieMetadataOfDoubleYAxisBarLine[];
}): IEChartOption => {
  const {
    serieNamesMap = {},
    serieIconsMap = {},
    serieMetadatas = [],
  } = options || {};
  const { seriesKeys, series, yAxisValueFormattersMap } =
    analysisSerieMetadatas(serieMetadatas);

  const dimensions: string[] = ['date', ...seriesKeys];
  const option: IEChartOption = merge({}, BASE_ECHART_OPTION, {
    grid: {
      ...BASE_ECHART_OPTION.grid,
      ...Y_AXIS_NAME_CURRENCY_GRID_OPTION,
      bottom: LEGEND_GRID_SPACING,
    },
    legend: [
      {
        ...BASE_ECHART_OPTION?.legend,
        show: false,
        height: 0,
      },
    ],
    tooltip: {
      ...BASE_ECHART_OPTION.tooltip,
      borderWidth: 0,
      backgroundColor: 'rgba(255, 255, 255, 0)',
      // 如需要设置hover浮窗内，纵向滚动时，可设置该配置为true，即可开启hover浮窗交互
      enterable: false,
      formatter: (params: any): string =>
        tootipFormatter(
          params,
          serieNamesMap,
          serieIconsMap,
          yAxisValueFormattersMap,
        ),
    },
    xAxis: {
      ...BASE_ECHART_OPTION.xAxis,
      axisLabel: {
        ...BASE_ECHART_OPTION.xAxis?.axisLabel,
        formatter: axisLabelOfXAxisFormatter,
      },
      data: [],
    },
    yAxis: [
      {
        // @ts-ignore
        ...BASE_ECHART_OPTION?.yAxis?.[0],
        ...Y_AXIS_NAME_CURRENCY_OPTION,
        alignTicks: true,
        axisLabel: {
          ...BASE_ECHART_OPTION?.yAxis?.[0]?.axisLabel,
          formatter: axisLabelOfLeftYAxisFormatter,
        },
      },
      {
        // @ts-ignore
        ...BASE_ECHART_OPTION?.yAxis?.[0],
        axisLabel: {
          ...BASE_ECHART_OPTION?.yAxis?.[0]?.axisLabel,
          formatter: axisLabelOfRightYAxisFormatter,
        },
      },
    ],
    series: [],
    dataset: {
      dimensions: [],
      source: [],
    },
  });

  option.series = series;
  // @ts-ignore
  option.dataset.dimensions = dimensions;
  return option;
};

/**
 * 获取双轴柱线图 动态配置，需要依赖数据动态变化的配置，包含x轴的data，dataset的source
 * @param data 源数据
 * @returns 双轴柱线图 数据相关的配置，需要依赖数据的配置，包含x轴的data，dataset的source
 */
const getChartDynamicOptionData = (
  data: any[],
): {
  sources: IEChartDataEntity[];
  xAxisData: string[];
} => {
  const dates: Set<string> = new Set();
  const sources: {
    date: string;
    [key: string]: string;
  }[] = [];
  let xAxisData: string[] = [];

  if (Array.isArray(data)) {
    for (const dataItem of data) {
      const date = dataItem?.date;
      if (!date) continue;
      dates.add(date);
      sources.push(dataItem);
    }
    xAxisData = Array.from(dates).sort((a, b) => {
      const aM = moment(a);
      const bM = moment(b);
      if (aM.isSame(bM)) return 0;
      return aM.isAfter(bM) ? 1 : -1;
    });
  }

  return {
    sources,
    xAxisData,
  };
};

/**
 * 获取初始legendSelected、初始配置项
 * @param options serie name映射集、serie icon映射集、serie配置的metadata
 * @returns 初始legendSelected、初始配置项
 */
const getChartInitialOptionDataAndInitialLegendSelected = (options: {
  // echart serie name 映射集
  serieNamesMap: {
    [key: string]: string;
  };
  // echart serie icon 映射集
  serieIconsMap: {
    [key: string]: string;
  };
  serieMetadatas: ISerieMetadataOfDoubleYAxisBarLine[];
}) => {
  const { serieNamesMap, serieIconsMap, serieMetadatas } = options || {};

  const initialLegendSelected = getChartLegendSelected(serieMetadatas);
  const initialOption = getChartStaticOptionData?.({
    serieNamesMap,
    serieIconsMap,
    serieMetadatas,
  });
  merge(initialOption, {
    legend: [
      {
        // @ts-ignore
        ...initialOption?.legend?.[0],
        selected: initialLegendSelected,
      },
    ],
  });

  return {
    initialLegendSelected,
    initialOption,
  };
};

const legendFormatter = (
  serieKey: string,
  map: {
    [key: string]: string;
  },
): string => map?.[serieKey] || serieKey;

const axisLabelOfXAxisFormatter = (name: string): string => {
  return getValidDate(name, 'YYYY-MM');
};

const axisLabelOfLeftYAxisFormatter = (payload: any): string => {
  return getFormatNumPlus(payload, 2, false);
};

const axisLabelOfRightYAxisFormatter = (payload: any): string => {
  return getFormatPercentPlus(payload, 0);
};


const tootipFormatter = (
  params: any,
  serieNamesMap: {
    [key: string]: string;
  },
  serieIconsMap: {
    [key: string]: string;
  },
  yAxisValueFormattersMap: {
    [key: string]: (...params: any[]) => any;
  },
): string => {
  const data = params?.[0]?.data;
  const title = getValidDate(data?.data?.date, 'YYYY-MM');
  let rowsHTMLStr = ''; // 第一列数据

  if (Array.isArray(params)) {
    for (let i = 0; i < params?.length; i++) {
      const t = params?.[i];
      const serieKey = t?.seriesName;
      let serieName = legendFormatter(serieKey, serieNamesMap);
      const serieValue = t?.value;
      // Actual or Forecast具体化
      if (serieName === 'Actual or Forecast' && t?.data?.data?.actual_or_forecast) {
        serieName = toFirstUpperCase(t?.data?.data?.actual_or_forecast);
      }
      const serieValueFormatter = yAxisValueFormattersMap?.[serieKey];
      const serieValueFormatted =
        serieValueFormatter?.(serieValue) || serieValue;
      const serieIcon = serieIconsMap?.[serieKey];
      const serieIconHTMLStr = !serieIcon
        ? ''
        : `<img src="${serieIcon}" width="16" height="16" style="margin: 0; margin-right: 5px !important; vertical-align: middle;" />`;
      let dotHTMLStr = `<span class='header-round' style=" background-color: ${t?.color};"></span>`;
      switch (serieName) {
        case 'Actual':
          dotHTMLStr = `<span class='header-round' style=" background-color: #A3A5B2;"></span>`;
          break;

        case 'Forecast':
          dotHTMLStr = `<div class='dbg-flex-img'><img src="${forecastSvg}"></div>`;
          break;

        case 'Budget':
          dotHTMLStr = `<div class='dbg-flex-img'><img src="${budgetSvg}"></div>`;
          break;

        default:
          break;
      }

      rowsHTMLStr += `
        <div class='dbg-flex margin-10'>
          <div class='dbg-flex'>
            ${dotHTMLStr}
            <div class='body-text' style="max-width: 124px; width: 124px;">${
              serieName || '-'
            }</div>
          </div>
          <div class='body-num'>${serieValueFormatted}</div>
        </div>
      `;
    }
  }

  return `
    <section class='echart-tooltip-style-content'>
      <div class='header-title'>${title || '-'}</div>
      <div class='project-content-box'>${
        rowsHTMLStr || '-'
      }</div>
    <section>
  `;
};

export {
  getChartDynamicOptionData,
  getChartInitialOptionDataAndInitialLegendSelected,
};
